import type { ExperimentPipeline } from 'types';
import { useEffect, useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ShieldAlert } from 'lucide-react';

type AutomationPanelProps = {
  pipeline: ExperimentPipeline;
  /** True when some stage holds a pairing the engine would refuse to start. */
  hasBlocking: boolean;
  isDirty: boolean;
  onSave: (settings: {
    name: string;
    description: string;
    automationEnabled: boolean;
    autoDeployEnabled: boolean;
  }) => Promise<boolean>;
};

/**
 * The pipeline's settings, and the two flips.
 *
 * Keyed on the pipeline in the parent so this never initialises its fields from an
 * unresolved query — a form seeded with blanks saves the blanks back, which is how the
 * schedule editor lost a start time once.
 */
export const AutomationPanel = ({ pipeline, hasBlocking, isDirty, onSave }: AutomationPanelProps) => {
  const [name, setName] = useState(pipeline.name);
  const [description, setDescription] = useState(pipeline.description ?? '');
  const [automationEnabled, setAutomationEnabled] = useState(pipeline.automationEnabled);
  const [autoDeployEnabled, setAutoDeployEnabled] = useState(pipeline.autoDeployEnabled);
  const [isSaving, setIsSaving] = useState(false);

  // The server is the authority on both flips: it refuses automation while the board has
  // a blocking collision, so a local toggle that was rejected has to fall back.
  useEffect(() => {
    setAutomationEnabled(pipeline.automationEnabled);
    setAutoDeployEnabled(pipeline.autoDeployEnabled);
  }, [pipeline.automationEnabled, pipeline.autoDeployEnabled]);

  const save = async (next?: Partial<{ automationEnabled: boolean; autoDeployEnabled: boolean }>) => {
    setIsSaving(true);
    const settings = {
      name: name.trim() || pipeline.name,
      description,
      automationEnabled: next?.automationEnabled ?? automationEnabled,
      autoDeployEnabled: next?.autoDeployEnabled ?? autoDeployEnabled,
    };
    // Turning the main flip off takes auto-deploy with it: the API refuses auto-deploy
    // without automation, since it is what performs the deploy.
    if (!settings.automationEnabled) settings.autoDeployEnabled = false;

    const ok = await onSave(settings);
    if (ok) {
      setAutomationEnabled(settings.automationEnabled);
      setAutoDeployEnabled(settings.autoDeployEnabled);
    }
    setIsSaving(false);
  };

  const blockedFromArming = hasBlocking && !automationEnabled;

  return (
    <section className="bg-panel border-line rounded-panel border" data-tour="pipelines-automation">
      {/*
        Collapsed by default once the pipeline is past Draft. Settings are read
        constantly while you set a pipeline up and almost never afterwards, and this
        card sits above the board — left open it pushes the board below the fold for
        the whole life of the pipeline.
      */}
      <Accordion type="single" collapsible defaultValue={pipeline.status === 'Draft' ? 'settings' : undefined}>
        <AccordionItem value="settings" className="border-b-0">
          <AccordionTrigger className="micro-label hover:text-foreground px-3 py-2.5 hover:no-underline">
            Pipeline settings
          </AccordionTrigger>

          <AccordionContent className="px-3 pb-3">
            {/* Across the width, not down the page — see the note on collapsing above. */}
            <div className="grid gap-x-3 gap-y-3 lg:grid-cols-2 xl:grid-cols-4">
              <div className="space-y-1.5">
                <Label htmlFor="pipeline-name" className="micro-label">
                  Name
                </Label>
                <Input
                  id="pipeline-name"
                  value={name}
                  onChange={(ev) => setName(ev.target.value)}
                  className="border-line bg-field h-9 rounded-lg text-body"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="pipeline-description" className="micro-label">
                  Description
                </Label>
                {/* Matches the name field's height rather than the textarea's 80px default;
                    still drag-resizable for anyone writing more than a line. */}
                <Textarea
                  id="pipeline-description"
                  className="border-line bg-field h-9 min-h-0 resize-y rounded-lg text-body"
                  value={description}
                  onChange={(ev) => setDescription(ev.target.value)}
                  placeholder="What is this programme testing?"
                />
              </div>

              <div className="border-line bg-panel2 rounded-card space-y-1.5 border p-2.5">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="pipeline-automation-enabled" className="text-body font-medium">
                    Run automatically
                  </Label>
                  <Switch
                    id="pipeline-automation-enabled"
                    checked={automationEnabled}
                    disabled={isSaving || blockedFromArming}
                    onCheckedChange={(checked) => save({ automationEnabled: checked })}
                  />
                </div>
                <p className="supporting">Off records what the engine would have done.</p>
                {blockedFromArming && (
                  <p className="text-danger flex items-start gap-1.5 text-micro font-medium">
                    <ShieldAlert className="mt-0.5 size-3.5 shrink-0" strokeWidth={1.5} />
                    Separate the conflicting experiments first.
                  </p>
                )}
                {isDirty && (
                  <p className="text-warn text-micro">Save the board first, or the engine runs what you last saved.</p>
                )}
              </div>

              <div className="border-line bg-panel2 rounded-card space-y-1.5 border p-2.5">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="pipeline-auto-deploy-enabled" className="text-body font-medium">
                    Deploy the winner
                  </Label>
                  <Switch
                    id="pipeline-auto-deploy-enabled"
                    checked={autoDeployEnabled}
                    disabled={isSaving || !automationEnabled}
                    onCheckedChange={(checked) => save({ autoDeployEnabled: checked })}
                  />
                </div>
                <p className="supporting">Auto-deploying a loser changes what every visitor sees.</p>
              </div>
            </div>

            <div className="mt-3 flex justify-end">
              <button
                type="button"
                disabled={isSaving}
                onClick={() => save()}
                className="bg-cta hover:bg-ctahover flex h-7 items-center rounded-lg px-3 text-meta font-medium text-white transition-colors duration-150 disabled:opacity-40"
              >
                {isSaving ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
};
